import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Command, Search, ArrowRight, User, Users, Star, CalendarDays, ClipboardList, FileText, Music4, X } from "lucide-react";

const PAGES = [
  { label: "Dashboard", to: "/dashboard", Icon: Command, kw: "home overview" },
  { label: "My Schedule", to: "/my-schedule", Icon: CalendarDays, kw: "personal calendar" },
  { label: "Calendar", to: "/calendar", Icon: CalendarDays, kw: "rehearsal calendar" },
  { label: "Auditions", to: "/auditions", Icon: Star, kw: "casting scoring" },
  { label: "Audition Day", to: "/audition-day", Icon: Star, kw: "checkin queue" },
  { label: "Script", to: "/script", Icon: FileText, kw: "pdf analyze acts scenes" },
  { label: "Scenes & Songs", to: "/scenes", Icon: Music4, kw: "songs scene list" },
  { label: "Cast Notes", to: "/cast-notes", Icon: FileText, kw: "notes messages" },
  { label: "Announcements", to: "/announcements", Icon: FileText, kw: "posts news" },
  { label: "People", to: "/contacts", Icon: Users, kw: "contacts directory roster" },
  { label: "Rehearsal Reports", to: "/rehearsal-reports", Icon: ClipboardList, kw: "SM reports actionables" },
  { label: "Documents", to: "/documents", Icon: FileText, kw: "files uploads" },
];

export default function CommandPalette({ open, onClose }) {
  const nav = useNavigate();
  const { productionId } = useAuth();
  const [q, setQ] = useState("");
  const [chars, setChars] = useState([]);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setQ("");
      setTimeout(() => inputRef.current?.focus(), 30);
      if (productionId) {
        api.get("/characters").then(({ data }) => setChars(data || [])).catch(() => setChars([]));
      }
    }
  }, [open, productionId]);

  const results = useMemo(() => {
    const s = q.trim().toLowerCase();
    const pageHits = PAGES.filter((p) => !s || (p.label + " " + p.kw).toLowerCase().includes(s)).slice(0, 8);
    const charHits = !s ? [] : chars.filter((c) => c.name.toLowerCase().includes(s)).slice(0, 6);
    return { pageHits, charHits };
  }, [q, chars]);

  if (!open) return null;

  const go = (to) => { onClose(); nav(to); };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-24 px-4" onClick={onClose} data-testid="command-palette">
      <div className="w-full max-w-lg rounded-2xl border border-white/15 bg-[#0F0F0F] shadow-[0_30px_80px_-20px_rgba(245,158,11,0.35)] overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-3 border-b border-white/10 flex items-center gap-2">
          <Search className="w-4 h-4 text-zinc-400 shrink-0" />
          <input
            ref={inputRef}
            data-testid="cmdk-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search pages, characters…"
            className="flex-1 bg-transparent outline-none text-sm text-zinc-100 placeholder:text-zinc-500"
            onKeyDown={(e) => {
              if (e.key === "Escape") onClose();
              if (e.key === "Enter") {
                const first = results.charHits[0] ? `/characters/${results.charHits[0].id}` : results.pageHits[0]?.to;
                if (first) go(first);
              }
            }}
          />
          <kbd className="text-[10px] text-zinc-500 border border-white/10 rounded px-1.5 py-0.5">Esc</kbd>
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {results.charHits.length > 0 && (
            <div>
              <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.24em] text-zinc-500">Characters</div>
              {results.charHits.map((c) => (
                <button key={c.id} data-testid={`cmdk-char-${c.id}`} onClick={() => go(`/characters/${c.id}`)}
                  className="w-full text-left px-3 py-2 hover:bg-gold/5 inline-flex items-center gap-2.5 group">
                  <User className="w-4 h-4 text-gold" />
                  <div className="flex-1 text-sm text-zinc-100 truncate">{c.name}</div>
                  <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-gold" />
                </button>
              ))}
            </div>
          )}
          <div>
            <div className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.24em] text-zinc-500">Pages</div>
            {results.pageHits.map((p) => (
              <button key={p.to} data-testid={`cmdk-page-${p.to}`} onClick={() => go(p.to)}
                className="w-full text-left px-3 py-2 hover:bg-gold/5 inline-flex items-center gap-2.5 group">
                <p.Icon className="w-4 h-4 text-zinc-400 group-hover:text-gold" />
                <div className="flex-1 text-sm text-zinc-100 truncate">{p.label}</div>
                <ArrowRight className="w-3.5 h-3.5 text-zinc-500 group-hover:text-gold" />
              </button>
            ))}
            {results.pageHits.length === 0 && results.charHits.length === 0 && (
              <div className="px-3 py-6 text-center text-xs text-zinc-500">No results</div>
            )}
          </div>
        </div>
        <div className="px-3 py-2 border-t border-white/10 text-[10px] text-zinc-500 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <kbd className="border border-white/10 rounded px-1.5 py-0.5">↑↓</kbd> navigate
            <kbd className="border border-white/10 rounded px-1.5 py-0.5">⏎</kbd> open
          </div>
          <div className="inline-flex items-center gap-1"><Command className="w-3 h-3" />K</div>
        </div>
      </div>
    </div>
  );
}
